import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Coffee, Droplets } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SectionHeader } from '../components/SectionHeader';

const VIENTO_BAG = 'https://customer-assets.emergentagent.com/job_609ac7f0-fef8-44cd-ad33-021d30e2aa8a/artifacts/qkx8xa96_b6c040da-374f-4f7f-bd0d-ecaa3234b556.jpg';
const GALLERY = [
  'https://customer-assets.emergentagent.com/job_609ac7f0-fef8-44cd-ad33-021d30e2aa8a/artifacts/ih55u8fq_IMG_8900.jpeg',
  'https://customer-assets.emergentagent.com/job_609ac7f0-fef8-44cd-ad33-021d30e2aa8a/artifacts/oe0nyps5_5307ea52-a186-4128-b0f7-68ec63aff46b.jpg',
  'https://customer-assets.emergentagent.com/job_609ac7f0-fef8-44cd-ad33-021d30e2aa8a/artifacts/lf9vlhzl_IMG_8718.jpeg'
];

const BEVERAGES = ['Lattes', 'Cappuccinos', 'Americanos', 'Matcha Lattes', 'Dirty Chai'];
const SYRUPS = ['Vanilla', 'Mocha', 'Rum Cake', 'Toasted Coconut Caramel', 'Honey Cinnamon', 'Seasonal Selections*'];

const ADD_ONS = [
  { name: 'Cold Brew Service', price: '$50', desc: 'Smooth, refreshing cold brew served on tap.' },
  { name: 'Coconut Water Bar', price: '$30', desc: 'Fresh coconut water for an island-inspired refreshment option.' },
  { name: 'Custom Signature Drink', price: '$50', desc: 'A personalized beverage created specifically for your event, brand, or occasion.' },
  { name: 'Additional Milk Options', price: '$50', desc: 'Add coconut, almond, or other specialty milk alternatives.' },
  { name: 'Decaf Espresso Service', price: '$50', desc: 'Offer guests a full espresso experience without the caffeine.' },
  { name: 'Custom Branded Cups', price: '$100', desc: 'Personalized cups featuring your company logo, event branding, or custom artwork.**' },
  { name: 'Fresh Pastries', price: 'Varies', desc: 'Available upon request. Pricing varies based on guest count and selection.' }
];

const WhatWeServe = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-page" data-testid="what-we-serve-page">
      <Header />

      {/* Intro */}
      <section className="section" style={{ paddingTop: '8rem' }}>
        <SectionHeader eyebrow="On The Menu" title="What We Serve" />
        <div style={{ maxWidth: '720px', margin: '0 auto', textAlign: 'center' }}>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            At Sandbar Coffee, we bring a full-service espresso and matcha bar directly to your event.
          </p>
        </div>
      </section>

      {/* Cielito Coffee feature */}
      <section className="section" style={{ background: 'var(--bg-section)' }} data-testid="coffee-feature">
        <div className="coffee-feature-grid">
          <div className="coffee-feature-photo">
            <img src={VIENTO_BAG} alt="Cielito Coffee — Viento single-origin Colombian coffee" />
          </div>
          <div className="coffee-feature-text">
            <span className="section-eyebrow">Proudly Serving Cielito Coffee</span>
            <h2 className="section-title" style={{ marginBottom: '1rem' }}>Coffee</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              At the heart of every Sandbar drink is Viento from Cielito Coffee, a Colombian coffee from Quindío featuring notes of honey, chocolate, and almond. Its smooth, balanced profile shines whether enjoyed in a classic cappuccino or one of our signature island-inspired creations.
            </p>
          </div>
        </div>
      </section>

      {/* Photo gallery */}
      <section className="section">
        <div className="serve-gallery">
          {GALLERY.map((src, i) => (
            <div className="serve-gallery-item" key={i}>
              <img src={src} alt="Sandbar Coffee handcrafted drinks" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* Handcrafted Beverages */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <SectionHeader eyebrow="Made To Order" title="Handcrafted Beverages" />
        <div style={{ maxWidth: '760px', margin: '0 auto 2.5rem', textAlign: 'center' }}>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Every Sandbar service includes a curated menu of handcrafted espresso, matcha, and chai beverages served hot or iced. Guests can enjoy timeless favorites like lattes, cappuccinos, matcha lattes, and dirty chai, or customize their drinks with our house-made syrups and seasonal specialties.
          </p>
        </div>
        <div className="grid-2" style={{ alignItems: 'stretch', maxWidth: '820px', margin: '0 auto' }}>
          <div className="card bev-card" data-testid="bev-beverages">
            <div className="bev-card-icon"><Coffee size={24} /></div>
            <h3 className="heading-2" style={{ marginBottom: '0.75rem' }}>Beverages</h3>
            <ul className="menu-list">
              {BEVERAGES.map((d) => (
                <li key={d}><span className="menu-dot" />{d}</li>
              ))}
            </ul>
          </div>

          <div className="card bev-card" data-testid="bev-flavors">
            <div className="bev-card-icon"><Droplets size={24} /></div>
            <h3 className="heading-2" style={{ marginBottom: '0.75rem' }}>House-Made Flavors</h3>
            <ul className="menu-list">
              {SYRUPS.map((s) => (
                <li key={s}><span className="menu-dot" />{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Enhance Your Experience */}
      <section className="section">
        <SectionHeader
          eyebrow="Add-Ons"
          title="Enhance Your Experience"
          subtitle="Optional extras to make your event one of a kind"
        />
        <div className="addon-menu">
          {ADD_ONS.map(({ name, price, desc }) => (
            <div className="addon-row" key={name} data-testid={`addon-${name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/-$/, '')}`}>
              <div className="addon-row-main">
                <span className="addon-row-name">{name}</span>
                <span className="addon-row-desc">{desc}</span>
              </div>
              <span className="addon-row-price">{price}</span>
            </div>
          ))}
        </div>
        <div style={{ maxWidth: '720px', margin: '2rem auto 0', textAlign: 'center' }}>
          <p className="caption" style={{ color: 'var(--text-muted)' }}>
            * Seasonal flavors vary throughout the year based on ingredient availability and special menu offerings.
          </p>
          <p className="caption" style={{ color: 'var(--text-muted)', marginTop: '0.4rem' }}>
            ** Custom cup pricing includes design setup. Minimum quantities may apply.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-band" data-testid="menu-cta-band">
        <div className="cta-band-inner">
          <h2 className="heading-1" style={{ color: 'white', marginBottom: '0.75rem' }}>Ready for a custom quote?</h2>
          <p className="body-large" style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '1.75rem' }}>
            Tell us about your event and we&rsquo;ll send over a custom quote, usually within 24 hours.
          </p>
          <Link to="/book" className="btn-primary cta-band-btn" data-testid="menu-cta-book-btn">
            Get Your Custom Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhatWeServe;
